import React, { useState, useEffect, useRef } from "react";
import { Comparator, comparatorDefs, SerializedCondition } from "../types";
import Select from "@/components/Select";
import useApi from "@/hooks/useApi";

export type AttributeCondition = {
  attribute: string;
  comparator: Comparator;
  value: string;
};

interface FieldOption {
  name: string;
  label: string;
  scope: "built-in" | "contact" | "conversation" | "workflow";
}

const BUILT_IN_FIELDS: FieldOption[] = [
  { name: "name", label: "Full Name", scope: "built-in" },
  { name: "firstName", label: "First Name", scope: "built-in" },
  { name: "lastName", label: "Last Name", scope: "built-in" },
  { name: "email", label: "Email", scope: "built-in" },
  { name: "phone", label: "Phone", scope: "built-in" },
  { name: "language", label: "Language (browser)", scope: "built-in" }
];

const SCOPE_LABELS: Record<string, string> = {
  "built-in": "Built-in",
  contact: "Contact",
  conversation: "Conversation",
  workflow: "Workflow"
};

const SCOPE_ORDER = ["built-in", "contact", "conversation", "workflow"] as const;

export function AttributeEditor({
  condition,
  onUpdate
}: {
  condition: SerializedCondition;
  onUpdate: (condition: SerializedCondition) => void;
}) {
  const attributeCondition = JSON.parse(
    condition.data || "{}"
  ) as AttributeCondition;

  const [fields, setFields] = useState<FieldOption[]>(BUILT_IN_FIELDS);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const api = useApi();

  useEffect(() => {
    async function loadFields() {
      try {
        const res = await api.messaging.get("ContactFieldDefinitions/list");
        if (res.ok) {
          const data = await res.json();
          const defs = Array.isArray(data)
            ? data
            : Array.isArray(data?.definitions)
              ? data.definitions
              : [];
          const custom: FieldOption[] = defs
            .filter((d: { name?: string; label?: string }) => d.name || d.label)
            .map((d: { name?: string; label?: string; scope?: string }) => ({
              name: d.name || d.label?.toLowerCase().replace(/\s+/g, "_") || "",
              label: d.label || d.name || "",
              scope: (d.scope as FieldOption["scope"]) || "conversation"
            }));
          setFields([...BUILT_IN_FIELDS, ...custom]);
        }
      } catch {
        // keep built-ins only
      }
    }
    loadFields();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  function update(data: AttributeCondition) {
    onUpdate({ ...condition, data: JSON.stringify(data) });
  }

  function selectField(field: FieldOption) {
    update({ ...attributeCondition, attribute: field.name });
    setOpen(false);
    setSearch("");
    setManualMode(false);
  }

  const isKnownField = fields.some((f) => f.name === attributeCondition.attribute);
  const effectiveManual = manualMode || (!!attributeCondition.attribute && !isKnownField);

  const currentField = fields.find((f) => f.name === attributeCondition.attribute);
  const displayLabel = currentField
    ? `${currentField.label} (${SCOPE_LABELS[currentField.scope]})`
    : attributeCondition.attribute || "";

  const filtered = search.trim()
    ? fields.filter(
        (f) =>
          f.name.toLowerCase().includes(search.toLowerCase()) ||
          f.label.toLowerCase().includes(search.toLowerCase())
      )
    : fields;

  return (
    <>
      {/* Attribute picker */}
      {effectiveManual ? (
        <div className="flex gap-1 items-center">
          <input
            type="text"
            placeholder="Variable name (e.g. contact.tier)"
            value={attributeCondition.attribute || ""}
            onChange={(e) =>
              update({ ...attributeCondition, attribute: e.target.value })
            }
            className="border rounded-md p-2 flex-1 text-sm"
          />
          <button
            type="button"
            className="text-xs text-blue-600 hover:underline whitespace-nowrap"
            onClick={() => {
              setManualMode(false);
              setOpen(true);
            }}
          >
            Pick field
          </button>
        </div>
      ) : (
        <div ref={containerRef} className="relative">
          <button
            type="button"
            className="w-full text-left border rounded-md p-2 text-sm bg-white hover:border-blue-400 flex items-center justify-between gap-2"
            onClick={() => setOpen(!open)}
          >
            <span className={displayLabel ? "text-slate-800" : "text-slate-400"}>
              {displayLabel || "Select a field or variable..."}
            </span>
            <svg
              className="w-4 h-4 text-slate-400 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {open && (
            <div className="absolute left-0 top-full mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-lg z-50 flex flex-col max-h-72">
              {/* Search */}
              <div className="p-2 border-b border-slate-100">
                <input
                  autoFocus
                  type="text"
                  placeholder="Search fields..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-blue-400"
                />
              </div>

              {/* Grouped list */}
              <div className="overflow-y-auto flex-1">
                {filtered.length === 0 && (
                  <div className="p-3 text-sm text-slate-400 text-center">
                    No fields found
                  </div>
                )}
                {SCOPE_ORDER.map((scope) => {
                  const items = filtered.filter((f) => f.scope === scope);
                  if (items.length === 0) return null;
                  return (
                    <div key={scope}>
                      <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                        {SCOPE_LABELS[scope]}
                      </div>
                      {items.map((f) => (
                        <button
                          key={f.name}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                          onClick={() => selectField(f)}
                        >
                          <code className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                            {`<<${f.name}>>`}
                          </code>
                          <span className="text-sm text-slate-700 truncate">
                            {f.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Footer — type manually + create link */}
              <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 rounded-b-lg flex items-center justify-between">
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-blue-600 hover:underline"
                  onClick={() => {
                    setOpen(false);
                    setManualMode(true);
                  }}
                >
                  Type manually...
                </button>
                <a
                  href="/contact_fields"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => setOpen(false)}
                >
                  + Create custom field
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Comparator */}
      <Select
        options={comparatorDefs}
        value={attributeCondition.comparator}
        onChange={(v) =>
          update({ ...attributeCondition, comparator: v as Comparator })
        }
      />

      {/* Value (hidden for exists/notExists) */}
      {!["exists", "notExists"].includes(attributeCondition.comparator) && (
        <input
          type="text"
          placeholder="Value"
          value={attributeCondition.value || ""}
          onChange={(e) =>
            update({ ...attributeCondition, value: e.target.value })
          }
          className="border rounded-md p-2 w-full text-sm"
        />
      )}
    </>
  );
}
